import {
  Button,
  Card,
  CardContent,
  H2,
  Icon,
  ListItemIconTile,
} from "@sellar-io/components";
import { useNavigate } from "react-router";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import {
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS,
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_DIRECTOR,
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_BANK_ACCOUNT,
  BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_TERMS,
  BUSINESS_INTEGRATION_SETTINGS,
} from "../../../constants/routes";
import { twMerge } from "tailwind-merge";
import { RyftOnboardingStep } from "./types";
import { useRyftNonHostedOnboarding } from "./components/RyftNonHostedOnboardingProvider.shared";

export const RyftNonHostedOnboarding = () => {
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const { onboardingSteps } = useRyftNonHostedOnboarding();

  const steps = ryftOnboardingSteps.map((step) => ({
    ...step,
    status: onboardingSteps[step.id],
  }));

  return (
    <>
      <NavigationBar
        title="Ryft onboarding"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={
          isTwoPanel ? undefined : () => navigate(BUSINESS_INTEGRATION_SETTINGS)
        }
      />
      <PageContainer className="mx-auto flex max-w-3xl flex-col gap-6 p-4 md:p-6">
        <header className="flex flex-col gap-2">
          <H2>Set up payouts with Ryft</H2>
          <p className="max-w-2xl text-sm leading-6 text-zinc-600 md:text-base">
            Complete these steps so Ryft can verify your business and send
            payouts to your bank account.
          </p>
        </header>

        <RyftOnboardingChecklist steps={steps} />
      </PageContainer>
    </>
  );
};

const RyftOnboardingChecklist = ({
  steps,
}: {
  steps: RyftOnboardingStep[];
}) => {
  const navigate = useNavigate();
  return (
    <Card className="border border-zinc-200 bg-white">
      <CardContent className="divide-y divide-zinc-200 p-0">
        {steps.map((step) => (
          <RyftOnboardingChecklistItem
            key={step.id}
            step={step}
            onContinue={() => {
              navigate(step.path);
            }}
          />
        ))}
      </CardContent>
    </Card>
  );
};

const RyftOnboardingChecklistItem = ({
  step,
  onContinue,
}: {
  step: RyftOnboardingStep;
  onContinue: (stepId: RyftOnboardingStep["id"]) => void;
}) => {
  const isUpcoming = step.status === "upcoming";

  return (
    <div
      className={twMerge(
        "flex items-start gap-3 p-4 md:items-center md:gap-4 md:p-5",
        isUpcoming && "opacity-50",
      )}
    >
      <ListItemIconTile
        icon={step.icon}
        className="border border-zinc-200 bg-zinc-50"
      />

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-zinc-900">{step.title}</p>
        <p className="mt-1 text-sm leading-5 text-zinc-600">
          {step.description}
        </p>
      </div>

      <RyftOnboardingStepControl step={step} onContinue={onContinue} />
    </div>
  );
};

const RyftOnboardingStepControl = ({
  step,
  onContinue,
}: {
  step: RyftOnboardingStep;
  onContinue: (stepId: RyftOnboardingStep["id"]) => void;
}) => {
  if (step.status === "completed") {
    return (
      <Icon
        icon="CHECKBOX_MARKED_CIRCLE"
        size={1.25}
        className="shrink-0 self-center text-emerald-600"
      />
    );
  }

  if (step.status === "current") {
    return (
      <Button
        color="slim"
        className="shrink-0 self-center"
        aria-label={`Continue ${step.title}`}
        onClick={onContinue ? () => onContinue(step.id) : undefined}
      >
        <span className="hidden sm:inline">Continue</span>
        <Icon icon="CHEVRON_RIGHT" size={0.8} />
      </Button>
    );
  }

  return null;
};

const ryftOnboardingSteps: Omit<RyftOnboardingStep, "status">[] = [
  {
    id: "create-business",
    icon: "STOREFRONT_OUTLINE",
    title: "Set up your business",
    description:
      "Review your legal business details so Ryft can create your payout account.",
    path: BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS,
  },
  {
    id: "add-business-director",
    icon: "ACCOUNT_MULTIPLE_OUTLINE",
    title: "Add a director",
    description:
      "Provide the details Ryft needs to verify a director of your business.",
    path: BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_DIRECTOR,
  },
  {
    id: "add-bank-account",
    icon: "BANK",
    title: "Add a bank account for payouts",
    description:
      "Choose the GBP bank account where you want Ryft to send your payouts.",
    path: BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_BUSINESS_BANK_ACCOUNT,
  },
  {
    id: "accept-terms",
    icon: "CLIPBOARD_CHECK_OUTLINE",
    title: "Accept the terms & conditions",
    description:
      "Review and accept Ryft's terms before your business can receive payouts.",
    path: BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING_TERMS,
  },
];
